import {
  ALLOWED_LOCALES,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_LOCALE,
  LatLong,
  Location,
  LocationSuggestions,
  RouteCffData,
  SuggestedLocationParams,
  TrendingDestinations,
} from '../common';
import { EnvConfig } from '../config';
import { isIn, isNotBlank, isPresent, sanitize } from '../utils';
import { HttpParams, httpRequest } from './httpUtils';
import { castResponse, CastResponse, Lock, Response, Status } from './types';
import { uniq, flatten } from 'lodash';

// == Utilities ================================================================

const fixLocationFields = (location: Location, isFromUserLocation = false): Location => {
  const { ajbPartner, sjbPartner, ...rest } = location as any;
  return { ...rest, AJBPartner: ajbPartner, SJBPartner: sjbPartner, isFromUserLocation };
};

const locationSearchLock: Lock = new Lock();
const getLocationByLatLongLock: Lock = new Lock();
const getSuggestedLocationsLock: Lock = new Lock();
const getTrendingDestinationsLock: Lock = new Lock();
const getLocationsLock: Lock = new Lock();
const getRouteCffsLock: Lock = new Lock();

const getSuggestedLocationParams = (locale: string): SuggestedLocationParams => {
  const sanitizedLocale = isIn(locale, ALLOWED_LOCALES) ? locale : DEFAULT_LOCALE;

  if (sanitizedLocale === DEFAULT_LOCALE || sanitizedLocale === 'en_001') {
    return { countryCode: DEFAULT_COUNTRY_CODE, locale: sanitizedLocale };
  }

  const splitted = sanitizedLocale.split('_');
  return { countryCode: splitted[1], locale: sanitizedLocale };
};

// == Interfaces ===============================================================

export interface LocationResponse {
  ok?: boolean;
  error?: any;
  errorMessage?: any;
  locale?: string;
}

export interface MultiLocationResponse extends LocationResponse {
  count?: number;
  items?: Location[];
}

export interface SingleLocationResponse extends LocationResponse {
  item?: Location;
}

export interface SuggestedLocationsResponse extends LocationResponse {
  departures?: MultiLocationResponse;
  destinations?: MultiLocationResponse;
}

export interface TrendingDestinationsResponse extends LocationResponse {
  locations?: Location[];
}

export interface RouteCffResponse extends LocationResponse {
  cffEconomy?: string;
  cffPremiumEconomy?: string;
  cffBusiness?: string;
  cffMixed?: string;
  hasYouth: boolean;
  hasSenior: boolean;
  errorMessage?: string;
  fpp?: boolean;
}

// == Services =================================================================

export const locationSearch = (
  { locationsUrl }: EnvConfig,
  query: string,
  locale: string,
  continentCode?: string
): Promise<Response<Location[]>> => {
  locationSearchLock.cancel();
  const params = { query, locale, continentCode };

  return httpRequest<MultiLocationResponse>({
    method: 'GET',
    url: `${locationsUrl}/locationmatch`,
    params: new HttpParams(params),
    timeout: 6000,
    retries: 2,
    lock: locationSearchLock,
  })
    .then(({ status, payload: { errorMessage, items } }: Response<MultiLocationResponse>) => ({
      status: isNotBlank(errorMessage) ? Status.ERROR : status,
      payload: items,
    }))
    .catch(castResponse as CastResponse<Location[]>)
    .finally(() => locationSearchLock.release());
};
export const cancelLocationSearch = (): boolean => locationSearchLock.cancel();

export const getLocationByLatLong = (
  { locationsUrl }: EnvConfig,
  { lat, long }: LatLong,
  locale: string
): Promise<Response<Location>> => {
  getLocationByLatLongLock.cancel();
  return httpRequest<SingleLocationResponse>({
    method: 'GET',
    url: `${locationsUrl}/geolocation`,
    params: new HttpParams({ locale, latitude: lat, longitude: long }),
    timeout: 6000,
    lock: getLocationByLatLongLock,
  })
    .then(({ status, payload: { errorMessage, item } }: Response<SingleLocationResponse>) => ({
      status: isNotBlank(errorMessage) ? Status.ERROR : status,
      payload: fixLocationFields(item, true),
    }))
    .catch(castResponse as CastResponse<Location>)
    .finally(() => getLocationByLatLongLock.release());
};
export const cancelGetLocationByLatLong = (): boolean => getLocationByLatLongLock.cancel();

export const getSuggestedLocations = (
  { locationsUrl }: EnvConfig,
  locale: string
): Promise<Response<LocationSuggestions>> => {
  getSuggestedLocationsLock.cancel();
  return httpRequest<SuggestedLocationsResponse>({
    method: 'GET',
    url: `${locationsUrl}/suggestedlocations`,
    params: new HttpParams(getSuggestedLocationParams(locale)),
    timeout: 6000,
    retries: 2,
    lock: getSuggestedLocationsLock,
  })
    .then(({ status, payload: { error, departures, destinations } }: Response<SuggestedLocationsResponse>) => ({
      status: isPresent(error) ? Status.ERROR : status,
      payload: { departures: departures?.items, destinations: destinations?.items },
    }))
    .catch(castResponse as CastResponse<LocationSuggestions>)
    .finally(() => {
      getSuggestedLocationsLock.release();
    });
};

export const getTrendingDestinations = (
  { recommendationUrl }: EnvConfig,
  locale: string,
  departureFrom: string
): Promise<Response<TrendingDestinations>> => {
  getTrendingDestinationsLock.cancel();
  return httpRequest<TrendingDestinationsResponse>({
    method: 'GET',
    url: `${recommendationUrl}/trending-destinations`,
    params: new HttpParams({
      locale: isIn(locale, ALLOWED_LOCALES) ? locale : DEFAULT_LOCALE,
      departureFrom,
      limit: 10,
    }),
    timeout: 6000,
    retries: 2,
    lock: getTrendingDestinationsLock,
  })
    .then(({ status, payload: { error, locations } }: Response<TrendingDestinationsResponse>) => ({
      status: isPresent(error) ? Status.ERROR : status,
      payload: { locations },
    }))
    .catch(castResponse as CastResponse<TrendingDestinations>)
    .finally(() => {
      getTrendingDestinationsLock.release();
    });
};

const getLocation = (
  locationsUrl: string,
  location: Partial<Location>,
  locale: string
): Promise<Response<Location>> => {
  if (!location?.locationCode) {
    return Promise.resolve({ status: Status.CANCELED });
  }
  return httpRequest<Location | LocationResponse>({
    method: 'GET',
    url: `${locationsUrl}/location/${location.locationCode}`,
    timeout: 6000,
    retries: 2,
    lock: getLocationsLock,
  })
    .then(({ payload }: Response<Location | LocationResponse>) => {
      if (isNotBlank((payload as LocationResponse).errorMessage)) {
        return { status: Status.ERROR };
      }

      const rawLocation: Location = fixLocationFields(payload as Location);
      // Use translated names
      const cityName: string = rawLocation[`cityName_${locale}`] || rawLocation.cityName;
      const country: string = rawLocation[`country_${locale}`] || rawLocation.country;
      const title: string = rawLocation[`title_${locale}`] || rawLocation.title;
      const destinationUrl: string = rawLocation[`destinationUrl_${locale}`] || rawLocation.destinationUrl;
      const countryCode: string = rawLocation.countryCode;
      // Remove excess and default translations (e.g. cityName_ko_KR, country_en_IT, title)
      const location: Location = Object.keys(rawLocation)
        .filter(
          (key: string) =>
            !key.startsWith('cityName') &&
            !key.startsWith('country') &&
            !key.startsWith('title') &&
            !key.startsWith('destinationUrl') &&
            !key.startsWith('countryCode')
        )
        .reduce(
          (acc: Location, key: string) => {
            acc[key] = rawLocation[key];
            return acc;
          },
          { cityName, country, title, destinationUrl, countryCode } as Location
        );
      return { status: Status.OK, payload: location };
    }) // eslint-disable-next-line
    .catch(castResponse as CastResponse<Location>);
};

export const getLocations = (
  { locationsUrl }: EnvConfig,
  locations: { origin?: Partial<Location>; destination?: Partial<Location> }[],
  locale: string,
  customLock?: Lock
): Promise<Response<Location>[]> => {
  const lock = customLock || getLocationsLock;
  const uniqueLocationCodes = uniq(
    flatten(locations.map((ond) => [ond?.origin?.locationCode, ond?.destination?.locationCode]))
  ).filter(Boolean);
  lock.cancel();
  return Promise.all(
    uniqueLocationCodes.map((locationCode) => getLocation(locationsUrl, { locationCode }, locale))
  ).finally(() => lock.release());
};
export const cancelGetLocations = (): boolean => getLocationsLock.cancel();

export const getRouteCffs = (
  { locationsUrl }: EnvConfig,
  { locationCode: originLocation }: Partial<Location>,
  { locationCode: destinationLocation }: Partial<Location>,
  isAward: boolean,
  locale: string,
  isCountryShopTestGroup: boolean
): Promise<Response<RouteCffData>> => {
  getRouteCffsLock.cancel();

  return httpRequest<RouteCffResponse>({
    method: 'GET',
    url: `${locationsUrl}/routecff`,
    params: new HttpParams(
      sanitize({ originLocation, destinationLocation, isAward, locale, isCountryShopTestGroup }, (value) => !value)
    ),
    timeout: 6000,
    retries: 2,
    lock: getRouteCffsLock,
  })
    .then(
      ({
        status,
        payload: { errorMessage, cffEconomy, cffPremiumEconomy, cffBusiness, cffMixed, hasYouth, hasSenior, fpp },
      }: Response<RouteCffResponse>) => {
        if (isNotBlank(errorMessage)) {
          return { status: Status.ERROR };
        }
        return {
          status,
          payload: {
            cffEconomy: Boolean(cffEconomy),
            cffPremiumEconomy: Boolean(cffPremiumEconomy),
            cffBusiness: Boolean(cffBusiness),
            cffMixed: Boolean(cffMixed),
            hasYouth: Boolean(hasYouth),
            hasSenior: Boolean(hasSenior),
            fppEnabled: Boolean(fpp), // If non-existing, defaulting to false
          },
        };
      }
    )
    .catch(castResponse as CastResponse<RouteCffData>)
    .finally(() => getRouteCffsLock.release());
};
export const cancelGetRouteCffs = (): boolean => getRouteCffsLock.cancel();
